import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkIcon from '@mui/icons-material/Link'
import {
  companyInviteLinkUrl,
  disableCompanyInviteLink,
  getCompanyInviteLink,
  inviteLinkErrorMessage,
  resetCompanyInviteLink,
  updateCompanyInviteLinkRole,
  type CompanyInviteLinkState,
} from '../../../services/CompanyInviteLinkService'
import { useActorPermissionKeys, useSurface } from '../../../hooks/usePermission'
import { useInviteRoles, type InviteRolesState } from '../../../hooks/useInviteRoles'
import { assignableRoles, effectiveDefaultRoleKey, roleNameOf } from '../utils/roleOptions'

/** The select's value for "follow the company default": a role key can't be empty. */
const FOLLOW_DEFAULT = ''

/**
 * One link anyone can use to join the company (SC-438 D14), with the role joiners get (SC-439).
 * Used on the onboarding invite step, which makes the link if there is none, and in the "Invite
 * link" tab of the invite dialog, which only shows it until someone turns it on
 * (`createIfMissing={false}`).
 *
 * The roles list (names, the default, what the person may assign) is one request per time the
 * card opens. A parent that already loaded it passes it as `inviteRoles`, so the onboarding step
 * does not ask twice.
 */
export default function WorkspaceInviteLinkCard({
  companyId,
  createIfMissing = true,
  inviteRoles,
}: {
  companyId: string
  createIfMissing?: boolean
  inviteRoles?: InviteRolesState
}) {
  const [token, setToken] = useState<string | null>(null)
  // The link is on, but the viewer may not assign its role, so cloud sends no token (SC-439).
  const [tokenHidden, setTokenHidden] = useState(false)
  // A role picked and waiting for the "creates a new link" confirmation.
  const [pendingRole, setPendingRole] = useState<string | null>(null)
  const [roleKey, setRoleKey] = useState<string | null>(null)
  const [effectiveRoleKey, setEffectiveRoleKey] = useState<string | undefined>(undefined)
  const [roleSaving, setRoleSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  // The link lets anyone join, so only people who may invite see it (SC-439); the cloud checks too.
  const mayInvite = useSurface('workspace-invite-link.card')
  const mayAssignRoles = useSurface('invite-dialog.role-picker')
  const actorPermissions = useActorPermissionKeys()
  const ownRoles = useInviteRoles(companyId, mayInvite && !inviteRoles)
  const rolesState = inviteRoles ?? ownRoles
  const rolesReady = rolesState.status === 'ready'

  // Old cloud returns neither role field: the link follows the default.
  const applyLink = (link: CompanyInviteLinkState) => {
    setToken(link.token)
    // Absent on an old cloud: the token is then always there when the link is on.
    setTokenHidden(link.tokenHidden === true && !link.token)
    setPendingRole(null)
    setRoleKey(typeof link.roleKey === 'string' && link.roleKey ? link.roleKey : null)
    setEffectiveRoleKey(link.effectiveRoleKey)
  }

  const run = async (call: () => Promise<CompanyInviteLinkState>) => {
    setLoading(true)
    setError('')
    try {
      applyLink(await call())
    } catch (err) {
      setError(inviteLinkErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!mayInvite) return
    void run(() => getCompanyInviteLink({ companyId, create: createIfMissing }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, createIfMissing, mayInvite])

  const url = token ? companyInviteLinkUrl(token) : ''

  const defaultRoleKey = rolesReady
    ? effectiveDefaultRoleKey({ defaultRoleKey: rolesState.defaultRoleKey })
    : undefined
  const joinRoleKey = effectiveRoleKey ?? roleKey ?? defaultRoleKey
  const joinRoleName = roleNameOf(rolesState.roles, joinRoleKey)
  const defaultRoleName = roleNameOf(rolesState.roles, defaultRoleKey)
  const choices =
    rolesReady && defaultRoleKey
      ? assignableRoles(rolesState.roles, actorPermissions, defaultRoleKey)
      : []
  // A role the person can't pick (they lack some of its permissions) still shows as the current
  // value, so the select is never left blank for a link someone else set.
  const currentOutsideList =
    roleKey && !choices.some((role) => role.key === roleKey)
      ? rolesState.roles.find((role) => role.key === roleKey)
      : undefined
  // A role that no longer exists is not in the list: cloud gives joiners the default then.
  const selectValue =
    roleKey && (currentOutsideList || choices.some((role) => role.key === roleKey))
      ? roleKey
      : FOLLOW_DEFAULT
  const canEditRole = mayAssignRoles && rolesReady && !!defaultRoleKey && !!token

  // Following the company default, or naming the role the link already gives, keeps the token. Any
  // other role makes cloud issue a new token and the old link stops working, so that pick waits
  // for a confirmation first.
  const pickRole = (value: string) => {
    if (value === FOLLOW_DEFAULT) void changeRole(value)
    else if (value === selectValue) setPendingRole(null)
    else if (selectValue === FOLLOW_DEFAULT && value === joinRoleKey) void changeRole(value)
    else setPendingRole(value)
  }

  const changeRole = async (value: string) => {
    setRoleSaving(true)
    setError('')
    try {
      applyLink(
        await updateCompanyInviteLinkRole({ companyId, role: value === FOLLOW_DEFAULT ? null : value })
      )
    } catch (err) {
      setError(inviteLinkErrorMessage(err))
    } finally {
      setRoleSaving(false)
      setPendingRole(null)
    }
  }

  // A hidden link still names its role, by the API name, so the viewer knows what it gives.
  const hiddenRoleText = joinRoleName
    ? `This link gives ${joinRoleName}.`
    : "This link gives a role you can't assign."

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy. Select the link and copy it yourself.')
    }
  }

  if (!mayInvite) return null

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }} data-testid="workspace-invite-link">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <LinkIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          Workspace invite link
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Anyone with this link can join while there are free seats.
        {token && joinRoleName ? ` People who join with this link get ${joinRoleName}.` : ''}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <CircularProgress size={20} aria-label="Loading the invite link" />
      ) : token ? (
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField size="small" fullWidth value={url} InputProps={{ readOnly: true }} inputProps={{ 'aria-label': 'Invite link' }} />
            <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={copy} sx={{ flexShrink: 0 }}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </Stack>
          {canEditRole && (
            <FormControl size="small" fullWidth>
              <InputLabel id="invite-link-role-label">Role for people who join</InputLabel>
              <Select
                labelId="invite-link-role-label"
                label="Role for people who join"
                value={selectValue}
                // The empty value is the "follow the default" item, which MUI shows only with this.
                displayEmpty
                disabled={roleSaving}
                onChange={(e) => pickRole(String(e.target.value))}
                data-testid="invite-link-role-picker"
              >
                <MenuItem value={FOLLOW_DEFAULT}>
                  {defaultRoleName
                    ? `Follow the company default (${defaultRoleName})`
                    : 'Follow the company default'}
                </MenuItem>
                {currentOutsideList && (
                  <MenuItem value={currentOutsideList.key} disabled>
                    {currentOutsideList.name}
                  </MenuItem>
                )}
                {choices.map((role) => (
                  <MenuItem key={role.key} value={role.key}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {pendingRole && (
            <Alert
              severity="warning"
              data-testid="invite-link-role-confirm"
              action={
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={roleSaving}
                    onClick={() => void changeRole(pendingRole)}
                  >
                    {`Change to ${roleNameOf(rolesState.roles, pendingRole) ?? 'this role'}`}
                  </Button>
                  <Button size="small" color="inherit" disabled={roleSaving} onClick={() => setPendingRole(null)}>
                    Cancel
                  </Button>
                </Stack>
              }
            >
              Changing the role creates a new link. The old link stops working.
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => run(() => resetCompanyInviteLink({ companyId }))}>
              Make a new link
            </Button>
            <Button size="small" color="error" onClick={() => run(() => disableCompanyInviteLink(companyId))}>
              Turn off
            </Button>
          </Stack>
        </Stack>
      ) : tokenHidden ? (
        <Stack spacing={1} data-testid="invite-link-hidden">
          <Typography variant="body2">
            {hiddenRoleText} Only someone who could invite people into that role can share it.
          </Typography>
          {/* Both only revoke, so they stay available to anyone who may invite. */}
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => run(() => resetCompanyInviteLink({ companyId }))}>
              Make a new link
            </Button>
            <Button size="small" color="error" onClick={() => run(() => disableCompanyInviteLink(companyId))}>
              Turn off
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Button variant="outlined" onClick={() => run(() => getCompanyInviteLink({ companyId }))}>
          Turn on invite link
        </Button>
      )}
    </Box>
  )
}
